import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText.js';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

/** Composante TextAnim de TimTools */
export default class TextAnim {
  /**
   * @param {HTMLElement} element - Élément HTML sur lequel la composante est instanciée
   */
  constructor(element) {
    this.element = element;
    this.trigger = element.closest('section') || element;
    this.options = {
      scrub: 0.6,
    };
    this.destroyed = false;
    this.motionPreference = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );
    this.onMotionChange = this.handleMotionChange.bind(this);

    gsap.registerPlugin(SplitText, ScrollTrigger);
    document.fonts.ready.then(() => {
      if (!this.destroyed) this.init();
    });
  }

  init() {
    this.motionPreference.addEventListener('change', this.onMotionChange);
    if (!this.motionPreference.matches) this.initSplit();
  }

  initSplit() {
    const explicitLines = [
      ...this.element.querySelectorAll(':scope > [data-text-line]'),
    ];
    if (explicitLines.length) {
      this.lineContext = gsap.context(() => {
        this.initAnim({ lines: explicitLines });
      }, this.element);
      return;
    }

    const children = [...this.element.children];
    const hasDirectText = [...this.element.childNodes].some(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim(),
    );
    // Ne pas oublier le texte placé avant un span highlight dans un titre mixte.
    const targets =
      !hasDirectText &&
      children.length &&
      children.every((child) => child.matches('span'))
        ? children
        : this.element;

    this.split = SplitText.create(targets, {
      type: 'lines',
      autoSplit: true,
      aria: 'auto',
      onSplit: (split) => this.initAnim(split),
    });
  }

  getOffset(line, index) {
    const bounds = line.getBoundingClientRect();
    const scale = (line.offsetWidth ? bounds.width / line.offsetWidth : 1) || 1;
    // Retrouve la position au repos même pendant un refresh au milieu de l'animation.
    const currentX = Number(gsap.getProperty(line, 'x')) || 0;
    const left = bounds.left - currentX * scale;
    const right = bounds.right - currentX * scale;
    const fromRight = index % 2 === 0;
    const distance = fromRight
      ? document.documentElement.clientWidth - left + 32
      : -right - 32;
    return distance / scale;
  }

  initAnim(split) {
    this.animation?.scrollTrigger?.kill();
    this.animation?.kill();
    const lines = split.lines;

    this.animation = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: this.trigger,
        start: 'top bottom',
        end: 'top 30%',
        scrub: this.options.scrub,
        invalidateOnRefresh: true,
      },
    });

    this.animation.fromTo(
      lines,
      {
        x: (index, line) => this.getOffset(line, index) / 20,
        alpha: 0,
      },
      {
        x: 0,
        alpha: 1,
        duration: 1,
      },
    );

    return this.animation;
  }

  handleMotionChange() {
    if (this.motionPreference.matches) {
      this.restore();
    } else if (!this.destroyed && !this.split && !this.lineContext) {
      this.initSplit();
    }
  }

  restore() {
    this.animation?.scrollTrigger?.kill();
    this.animation?.kill();
    this.split?.revert();
    this.lineContext?.revert();
    this.lineContext = null;
    this.animation = null;
    this.split = null;
  }

  destroy() {
    this.destroyed = true;
    this.motionPreference.removeEventListener('change', this.onMotionChange);
    this.restore();
  }
}
